<script lang="ts" setup>
import { computed, ref, onMounted, onUnmounted } from 'vue'

import { Pagination, PaginationList, PaginationPrev, PaginationNext, PaginationItem, PaginationEllipsis, BlockContainer } from '@toni/brand-ui'
import { usePageStore } from '@toni/rendering/src/stores/page'

import { PageMeta } from '../../utils/page-meta-schema'
import { usePageBlockWidth } from '../../composables/use-page-block-width.ts'

const props = defineProps<{
  pageMeta: PageMeta
}>()

const pageMeta = computed(() => props.pageMeta)
// this itemsPerPage value is set to 12 (Radix Vue default is 10) and might come in the future from the composer, that's why hardcoded for now
const itemsPerPage = 12
const computedTotal = computed(() => (pageMeta.value.numPages ?? 0) * itemsPerPage)
const currentPage = props.pageMeta.page ?? 1
const siblingCount = ref(2)

const getViewportWidth = () => window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth

const updateSiblingCount = (viewportWidth: number) => {
  siblingCount.value = viewportWidth >= 1040 ? 2 : 0
}

const handleResize = () => {
  updateSiblingCount(getViewportWidth())
}

onMounted(() => {
  updateSiblingCount(getViewportWidth())
  window.addEventListener('resize', handleResize)
})

onUnmounted(() => {
  window.removeEventListener('resize', handleResize)
})
const pageStore = usePageStore()
const size = computed(() => usePageBlockWidth(pageStore.pageData?.pageMeta, pageStore.isHome, pageStore.brandFromStore).value)
</script>

<template>
  <BlockContainer :size="size"
                  class="items-center">
    <Pagination v-model:page="currentPage"
                :total="computedTotal"
                :sibling-count="siblingCount"
                :items-per-page="itemsPerPage"
                show-edges
                :default-page="1"
                class="pt-8 pb-6">
      <PaginationList v-slot="{ items }"
                      class="flex items-center gap-1">
        <PaginationPrev :baseurl="pageMeta.url ?? ''"
                        :current-page="currentPage" />

        <template v-for="(item, index) in items">
          <PaginationItem v-if="item.type === 'page'"
                          :key="`pagination-item-${index}`"
                          :baseurl="pageMeta.url"
                          :page-number="item.value"
                          :current-page="currentPage" />
          <PaginationEllipsis v-else
                              :key="item.type"
                              :index="index" />
        </template>

        <PaginationNext :baseurl="pageMeta.url ?? ''"
                        :current-page="currentPage"
                        :num-pages="pageMeta.numPages" />
      </PaginationList>
    </Pagination>
  </BlockContainer>
</template>
